import { LiveVideoService, LiveVideoConfig, LiveVideoProvider, GetCompletedVideosOptions, LiveSearchParams } from '../../types/lives';
import { YouTubeProvider } from './providers/youtube';
import { APIProvider } from './providers/api';
import { VideoContent } from '../../types/content';
import { ApiResponse } from '../../types/api';
import { env } from '../../config/env';

export class LiveVideoManager implements LiveVideoService {
  private liveProvider: YouTubeProvider | null = null;
  private readonly apiProvider: APIProvider;
  private readonly config: LiveVideoConfig;

  constructor(config: LiveVideoConfig) {
    this.config = config;
    this.apiProvider = new APIProvider();

    if (config.enabled) {
      try {
        this.liveProvider = this.createProvider(config.provider);
      } catch (error) {
        console.warn('Failed to initialize live video provider:', error);
      }
    } else {
      console.warn('Live video service is disabled by configuration');
    }
  }

  private createProvider(provider: LiveVideoProvider): YouTubeProvider | null {
    switch (provider) {
      case 'youtube':
        if (!env.providers.youtube.enabled) {
          console.warn('YouTube provider is disabled by configuration');
          return null;
        }
        return new YouTubeProvider();
      default:
        console.warn(`Unsupported live video provider: ${provider}`);
        return null;
    }
  }

  public async getLiveVideos(): Promise<VideoContent | null> {
    if (!this.liveProvider) {
      console.warn('No live video provider available');
      return null;
    }
    return this.liveProvider.getLiveVideos();
  }

  public async getCompletedVideos(options: GetCompletedVideosOptions): Promise<ApiResponse<VideoContent>> {
    return this.apiProvider.getCompletedVideos(options);
  }

  public async searchLives(params: LiveSearchParams): Promise<ApiResponse<VideoContent>> {
    return this.apiProvider.searchLives(params);
  }

  public async getLiveById(id: string): Promise<VideoContent> {
    return this.apiProvider.getLiveById(id);
  }

  public setProvider(provider: LiveVideoProvider): void {
    try {
      const newProvider = this.createProvider(provider);
      if (newProvider) {
        this.liveProvider = newProvider;
      }
    } catch (error) {
      console.error('Failed to set live video provider:', error);
    }
  }

  public getConfig(): LiveVideoConfig {
    return this.config;
  }
}

// Create and export the default instance with YouTube provider for live streams
export const liveVideoService = new LiveVideoManager({
  provider: 'youtube',
  enabled: true // Always enable the service, let the provider handle its own availability
});

export * from '../../types/lives'; 